import React, { useState } from "react";

import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "~/components/ui/sheet";
import { clients, purchaseOrders } from "~/data/mockData";
import { toast } from "~/hooks/use-toast";
import { PaymentRequestStatus, PurchaseOrder } from "~/types/user";
import { formatCurrency, getStatusColor, getStatusText } from "~/utils/helpers";

const PaymentRequests = () => {
  const [selectedOrder, setSelectedOrder] = useState<PurchaseOrder | null>(
    null
  );
  const [openSheet, setOpenSheet] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [rejectionDialogOpen, setRejectionDialogOpen] = useState(false);

  const columns: PaymentRequestStatus[] = [
    "new",
    "pending_review",
    "accepted",
    "cancelled",
  ];

  // Group orders by status
  const ordersByStatus = purchaseOrders.reduce(
    (acc, order) => {
      const status =
        order.status === "pending_approval"
          ? "new"
          : order.status === "approved"
            ? "accepted"
            : order.status === "rejected"
              ? "cancelled"
              : "pending_review";

      if (!acc[status]) {
        acc[status] = [];
      }

      acc[status].push(order);
      return acc;
    },
    {} as Record<PaymentRequestStatus, PurchaseOrder[]>
  );

  const handleCardClick = (order: PurchaseOrder) => {
    setSelectedOrder(order);
    setOpenSheet(true);
  };

  const handleAccept = () => {
    toast({
      title: "Nota Aceptada",
      description: `La nota ${selectedOrder?.id} ha sido aprobada.`,
    });

    setOpenSheet(false);
  };

  const handleReject = () => {
    if (rejectionReason.trim() === "") {
      toast({
        title: "Rechazo Cancelado",
        description: "Por favor proporcione un motivo para el rechazo.",
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Nota Rechazada",
      description: `La nota ${selectedOrder?.id} ha sido rechazada.`,
      variant: "destructive",
    });

    setRejectionDialogOpen(false);
    setOpenSheet(false);
    setRejectionReason("");
  };

  const getClient = (clientId: string) => {
    return clients.find((client) => client.id === clientId);
  };

  // Spanish status translations
  const statusTextSpanish: Record<PaymentRequestStatus, string> = {
    new: "Nuevas",
    pending_review: "En Revisión",
    accepted: "Aceptadas",
    cancelled: "Canceladas",
  };

  return (
    <div className="space-y-6">
      <Card className="overflow-hidden rounded-2xl shadow-md">
        <CardHeader>
          <CardTitle className="text-xl font-bold">
            Tablero de Solicitudes de Pago
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
            {columns.map((status) => (
              <div key={status} className="space-y-3">
                <div className="flex items-center space-x-2">
                  <div
                    className={`h-3 w-3 rounded-full ${getStatusColor(status)}`}
                  ></div>
                  <h3 className="font-medium">{statusTextSpanish[status]}</h3>
                  <span className="ml-1 text-muted-foreground">
                    ({ordersByStatus[status]?.length || 0})
                  </span>
                </div>

                <div className="max-h-[70vh] min-h-[50vh] space-y-3 overflow-y-auto p-1">
                  {ordersByStatus[status]?.map((order) => (
                    <Card
                      key={order.id}
                      className="cursor-pointer overflow-hidden rounded-xl shadow-sm transition-shadow hover:shadow-md"
                      onClick={() => handleCardClick(order)}
                    >
                      <CardContent className="p-4">
                        <div className="space-y-2 text-sm">
                          <div className="font-medium">{order.id}</div>
                          <div className="text-muted-foreground">
                            {order.clientName}
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">
                              Fecha:
                            </span>
                            <span>{order.createdAt}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">
                              Total:
                            </span>
                            <span className="font-medium">
                              {formatCurrency(order.total)}
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}

                  {(!ordersByStatus[status] ||
                    ordersByStatus[status].length === 0) && (
                    <div className="flex h-20 items-center justify-center rounded-xl border-2 border-dashed text-sm text-muted-foreground">
                      Sin notas
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <Sheet open={openSheet} onOpenChange={setOpenSheet}>
            <SheetContent className="w-full overflow-y-auto rounded-l-2xl sm:max-w-xl">
              <SheetHeader className="mb-6">
                <SheetTitle>Detalles de Nota de Venta</SheetTitle>
                <SheetDescription>
                  ID de Nota: {selectedOrder?.id}
                </SheetDescription>
              </SheetHeader>

              {selectedOrder && (
                <div className="space-y-6">
                  <div className="space-y-3">
                    <h3 className="text-lg font-medium">
                      Información del Cliente
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-muted-foreground">Empresa</p>
                        <p className="font-medium">
                          {selectedOrder.clientName}
                        </p>
                      </div>

                      <div>
                        <p className="text-sm text-muted-foreground">
                          ID del Cliente
                        </p>
                        <p className="font-medium">{selectedOrder.clientId}</p>
                      </div>
                    </div>

                    {getClient(selectedOrder.clientId) && (
                      <Card className="overflow-hidden rounded-xl bg-muted/30 shadow-sm">
                        <CardContent className="p-4">
                          <div className="grid grid-cols-3 gap-y-3 text-sm">
                            <div className="col-span-3 mb-1">
                              <h4 className="font-medium">
                                Antecedentes del Cliente
                              </h4>
                            </div>

                            <div>
                              <p className="text-muted-foreground">
                                Puntaje de Crédito
                              </p>
                              <p
                                className={`font-medium ${getClient(selectedOrder.clientId)?.creditScore || 0 > 85 ? "text-green-600" : "text-amber-600"}`}
                              >
                                {getClient(selectedOrder.clientId)?.creditScore}
                                /100
                              </p>
                            </div>

                            <div>
                              <p className="text-muted-foreground">
                                Puntaje de Riesgo
                              </p>
                              <p
                                className={`font-medium ${getClient(selectedOrder.clientId)?.riskScore || 0 < 25 ? "text-green-600" : "text-amber-600"}`}
                              >
                                {getClient(selectedOrder.clientId)?.riskScore}
                                /100
                              </p>
                            </div>

                            <div>
                              <p className="text-muted-foreground">
                                Historial de Pagos
                              </p>
                              <p
                                className={`font-medium capitalize ${getClient(selectedOrder.clientId)?.paymentHistory === "good" ? "text-green-600" : getClient(selectedOrder.clientId)?.paymentHistory === "average" ? "text-amber-600" : "text-red-600"}`}
                              >
                                {getClient(selectedOrder.clientId)
                                  ?.paymentHistory === "good"
                                  ? "Bueno"
                                  : getClient(selectedOrder.clientId)
                                        ?.paymentHistory === "average"
                                    ? "Promedio"
                                    : "Deficiente"}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </div>

                  <div className="space-y-3">
                    <h3 className="text-lg font-medium">Detalles de la Nota</h3>
                    <div>
                      <div className="flex justify-between border-b py-1 text-sm">
                        <span className="text-muted-foreground">Creada</span>
                        <span>{selectedOrder.createdAt}</span>
                      </div>
                      <div className="flex justify-between border-b py-1 text-sm">
                        <span className="text-muted-foreground">Vendedor</span>
                        <span>{selectedOrder.sellerName}</span>
                      </div>
                      <div className="flex justify-between border-b py-1 text-sm">
                        <span className="text-muted-foreground">
                          Dirección de Entrega
                        </span>
                        <span className="max-w-[65%] text-right">
                          {selectedOrder.deliveryAddress}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <h3 className="text-lg font-medium">
                      Artículos de la Nota
                    </h3>
                    <div className="space-y-3">
                      {selectedOrder.items.map((item, index) => (
                        <div key={index} className="border-b pb-2 text-sm">
                          <div className="flex justify-between">
                            <span className="font-medium">
                              {item.productName}
                            </span>
                            <span className="text-right">
                              {formatCurrency(
                                item.quantity * item.pricePerUnit
                              )}
                            </span>
                          </div>
                          <div className="flex justify-between text-muted-foreground">
                            <span>
                              {item.quantity} unidades ×{" "}
                              {formatCurrency(item.pricePerUnit)}
                            </span>
                          </div>
                        </div>
                      ))}

                      <div className="flex justify-between pt-2 text-lg font-medium">
                        <span>Total</span>
                        <span>{formatCurrency(selectedOrder.total)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button
                      className="flex-1 rounded-full bg-green-600 hover:bg-green-700"
                      onClick={handleAccept}
                    >
                      Aceptar
                    </Button>

                    <Dialog
                      open={rejectionDialogOpen}
                      onOpenChange={setRejectionDialogOpen}
                    >
                      <DialogTrigger asChild>
                        <Button
                          variant="destructive"
                          className="flex-1 rounded-full"
                        >
                          Rechazar
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="rounded-2xl sm:max-w-[425px]">
                        <DialogHeader>
                          <DialogTitle>Rechazar Nota</DialogTitle>
                          <DialogDescription>
                            Por favor proporcione un motivo para rechazar esta
                            nota.
                          </DialogDescription>
                        </DialogHeader>
                        <div className="py-4">
                          <Input
                            className="rounded-xl"
                            placeholder="Ingrese el motivo del rechazo"
                            value={rejectionReason}
                            onChange={(e) => setRejectionReason(e.target.value)}
                          />
                        </div>
                        <DialogFooter>
                          <Button
                            onClick={() => setRejectionDialogOpen(false)}
                            variant="outline"
                            className="rounded-full"
                          >
                            Cancelar
                          </Button>
                          <Button
                            onClick={handleReject}
                            variant="destructive"
                            className="rounded-full"
                          >
                            Confirmar Rechazo
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              )}
            </SheetContent>
          </Sheet>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentRequests;
